import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalOrganizations: number
  totalProjects: number
  totalRequests: number
  totalTickets: number
  totalMembers: number
  totalGuests: number
  systemHealth: number
  newRequestsToday: number
  monthlyRevenue: number
}

interface Activity {
  type: string
  title: string
  description: string
  timestamp: string
}

interface TopOrganization {
  id: number
  name: string
  industry_name: string
  project_count: number
  revenue: number
}

interface GrowthMetrics {
  userGrowth: Array<{ month: string; user_count: number }>
  revenueGrowth: Array<{ month: string; revenue: number }>
}

interface SystemAlert {
  type: 'warning' | 'info' | 'error'
  title: string
  message: string
  timestamp: string
}

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/dashboard',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Dashboard'],
  endpoints: (builder) => ({
    getDashboardStats: builder.query<DashboardStats, void>({
      query: () => '/stats',
      transformResponse: (response: any) => response.data,
      providesTags: ['Dashboard'],
    }),
    getRecentActivity: builder.query<Activity[], number | void>({
      query: (limit = 10) => `/activity?limit=${limit}`,
      transformResponse: (response: any) => response.data,
      providesTags: ['Dashboard'],
    }),
    getTopOrganizations: builder.query<TopOrganization[], number | void>({
      query: (limit = 5) => `/organizations/top?limit=${limit}`,
      transformResponse: (response: any) => response.data,
      providesTags: ['Dashboard'],
    }),
    getGrowthMetrics: builder.query<GrowthMetrics, void>({
      query: () => '/metrics/growth',
      transformResponse: (response: any) => response.data,
      providesTags: ['Dashboard'],
    }),
    getSystemAlerts: builder.query<SystemAlert[], void>({
      query: () => '/alerts',
      transformResponse: (response: any) => response.data,
      providesTags: ['Dashboard'],
    }),
  }),
})

export const {
  useGetDashboardStatsQuery,
  useGetRecentActivityQuery,
  useGetTopOrganizationsQuery,
  useGetGrowthMetricsQuery,
  useGetSystemAlertsQuery,
} = dashboardApi 